import { AsyncLoaderTemplate } from '@shared/Frontend/asyncLoader'
import { StrictOmit } from '@shared/Frontend/utils/strictOmit'
import { fieldReplacer, fieldUpdater, partialUpdater } from '@shared/Frontend/utils/updaters'
import { AsyncState, Maybe, Template, unit } from 'ballerina-core'
import React, { ReactNode } from 'react'
import { BlocksContext, BlocksForeignMutationsExpected, BlocksTemplate, BlocksView } from './blocks/blocks.template'
import { getPage, getPagePreview } from './cmsPage.api'
import { CMSPage, CMSPageHeader, CMSPageState } from './cmsPage.state'
import { FiltersContext, FiltersWrapperTemplate, FiltersView } from './filters/filters.template'
import { TeasersContext, TeaserState, TeasersTemplate, TeasersView } from './teasers/teasers.template'
import { TeasersAsync } from './teasers/teasers.state'

export type CMSPageContext = BlocksContext &
  StrictOmit<FiltersContext & TeasersContext, 'slug' | 'type'> &
  (
    | { preview: true; bundle: string; nodeId: number; nodeRevision: number; token: string }
    | { preview: false; slug?: string; language?: string }
  )
export type CMSPageForeignMutationsExpected = BlocksForeignMutationsExpected
export type CMSPageView = {
  NotFoundPage: () => ReactNode
  Loader: (_ : { type?: "inline" | "full"}) => ReactNode
  CMSPage: (
    _: {
      header: CMSPageHeader;
      breadcrumbs?: { url: string; title: string }[],
      metaTitle?: string;
      children: ReactNode;
      gridOrList?: boolean;
      kind: string;
    } & (
      | ({ variety: 'overview'; totalItems: 'loading' | number } & (
          | { type: 'search'; searchQuery: string; updateQuery: (_: string) => void }
          | { type: 'person' | 'news' | 'standpoints' | 'ourview' }
        ))
      | { variety: 'other'; sidebar?: ReactNode }
    )
  ) => ReactNode
} & BlocksView &
  FiltersView &
  TeasersView

export const CMSPageTemplate = Template.Default<
  CMSPageContext,
  CMSPageState,
  CMSPageForeignMutationsExpected,
  CMSPageView
>(({ context, setState, foreignMutations, view }) => (
  <>
    {context.preview ? (
      <CMSPageLoaderTemplate
        context={{
          ...context,
          promise: () => getPagePreview(context.bundle, context.nodeId, context.nodeRevision, context.token),
        }}
        setState={setState}
        foreignMutations={unit}
        view={unit}
      />
    ) : (
      <CMSPageLoaderTemplate
        key={`${context.language ?? ''}/${context.slug ?? ''}`}
        context={{ ...context, promise: () => getPage(context.slug) }}
        setState={setState}
        foreignMutations={unit}
        view={unit}
      />
    )}

    {context.kind === 'loaded' ? (
      context.value == undefined ? (
        <view.NotFoundPage />
      ) : (
        <MappedLoadedCMSPageTemplate
          context={{ ...context, ...context.value }}
          setState={setState}
          foreignMutations={foreignMutations}
          view={view}
        />
      )
    ) : (
      <view.Loader type="full" />
    )}
  </>
))

const CMSPageLoaderTemplate = AsyncLoaderTemplate<Maybe<CMSPage>>()

const LoadedCMSPageTemplate = Template.Default<CMSPageContext, CMSPage, CMSPageForeignMutationsExpected, CMSPageView>(
  ({ context, setState, foreignMutations, view }) => {
    switch (context.kind) {
      case 'generic': {
        const sidebarBlock = context.blocks.find(b => b.kind === 'sidebarNavigation')
        const blocksContext = sidebarBlock
          ? { ...context, blocks: context.blocks.filter(b => b.kind !== 'sidebarNavigation') }
          : context
        const sidebar = sidebarBlock
          ? <view.SidebarNavigationBlock {...sidebarBlock} id="sidebar-navigation" />
          : undefined
        return (
          <view.CMSPage {...context} variety="other" sidebar={sidebar}>
            <MappedBlocksTemplate
              context={blocksContext}
              setState={setState}
              foreignMutations={foreignMutations}
              view={view}
            />
          </view.CMSPage>
        )
      }
      case 'home':
      case 'about':
      case 'contact':
      case 'cookies':
      case 'terms':
      case 'privacy':
      case 'person':
      case 'news':
      case 'standpoints':
      case 'ourview':
        return (
          <view.CMSPage {...context} variety="other">
            <MappedBlocksTemplate
              context={context}
              setState={setState}
              foreignMutations={foreignMutations}
              view={view}
            />
          </view.CMSPage>
        )
      case 'search':
      case 'news overview':
      case 'standpoints overview':
      case 'ourview overview':
      case 'person overview': {
        const totalItems =
          AsyncState.Operations.hasValue(context.teasers.items) && context.teasers.items.value
            ? context.teasers.items.value.totalCount
            : 'loading'
        switch (context.kind) {
          case 'search':
            return (
              <view.CMSPage
                {...context}
                variety="overview"
                type="search"
                searchQuery={context.searchQuery}
                updateQuery={(newQuery): void =>
                  setState((state) =>
                    state.kind == 'search'
                      ? fieldReplacer<CMSPage & { kind: 'search' }>()('searchQuery')(newQuery)(state)
                      : state
                  )
                }
                totalItems={totalItems}
              >
                <MappedFiltersTemplate
                  context={{ ...context, ...context.filters, type: 'search' }}
                  setState={setState}
                  foreignMutations={foreignMutations}
                  view={view}
                />
                <MappedTeasersTemplate
                  context={{ ...context.teasers, page: context.page, slug: context.slug, filters: context.filters, type: 'search' }}
                  setState={setState}
                  foreignMutations={foreignMutations}
                  view={view}
                />
              </view.CMSPage>
            )
          case 'news overview':
          case 'person overview': {
            const type = context.kind == 'news overview' ? 'news' : 'person'
            return (
              <view.CMSPage {...context} variety="overview" type={type} totalItems={totalItems}>
                <MappedFiltersTemplate
                  context={{ ...context, ...context.filters, type }}
                  setState={setState}
                  foreignMutations={foreignMutations}
                  view={view}
                />
                <MappedTeasersTemplate
                  context={{ ...context, ...context.teasers, type }}
                  setState={setState}
                  foreignMutations={foreignMutations}
                  view={view}
                />
              </view.CMSPage>
            )
          }
          case 'standpoints overview': {
            const type = 'standpoints'
            return (
              <view.CMSPage {...context} variety="overview" type={type} totalItems={totalItems}>
                <MappedFiltersTemplate
                  context={{ ...context, ...context.filters, type }}
                  setState={setState}
                  foreignMutations={foreignMutations}
                  view={view}
                />
                <MappedTeasersTemplate
                  context={{ ...context, ...context.teasers, type }}
                  setState={setState}
                  foreignMutations={foreignMutations}
                  view={view}
                />
              </view.CMSPage>
            )
          }
          case 'ourview overview': {
            const type = 'our-other-views'
            return (
              <view.CMSPage {...context} variety="overview" type={'ourview'} totalItems={totalItems}>
                <MappedFiltersTemplate
                  context={{ ...context, ...context.filters, type }}
                  setState={setState}
                  foreignMutations={foreignMutations}
                  view={view}
                />
                <MappedTeasersTemplate
                  context={{ ...context, ...context.teasers, type }}
                  setState={setState}
                  foreignMutations={foreignMutations}
                  view={view}
                />
              </view.CMSPage>
            )
          }
        }
      }
    }
  }
)

const MappedLoadedCMSPageTemplate = LoadedCMSPageTemplate.mapState((updater) =>
  AsyncState.Operations.map(Maybe.Operations.map(updater))
)

const MappedBlocksTemplate = BlocksTemplate.mapState<CMSPage>(
  (updater) =>
    (state): CMSPage =>
      state.kind == 'generic' ||
      state.kind == 'home' ||
      state.kind == 'about' ||
      state.kind == 'contact' ||
      state.kind == 'cookies' ||
      state.kind == 'terms' ||
      state.kind == 'privacy' ||
      state.kind == 'person' ||
      state.kind == 'news' ||
      state.kind == 'standpoints' ||
      state.kind == 'ourview'
        ? partialUpdater(updater)(state)
        : state
)

const MappedFiltersTemplate = FiltersWrapperTemplate.mapState<CMSPage>(
  (updater) =>
    (state): CMSPage =>
      state.kind == 'news overview' || state.kind == 'person overview' || state.kind == 'search'
        ? fieldUpdater<CMSPage & { kind: 'news overview' | 'person overview' | 'standpoints overview' | 'ourview overview' | 'search' }>()('filters')(updater)(state)
        : state
)

const MappedTeasersTemplate = TeasersTemplate.mapState<CMSPage>(
  (updater) =>
    (state): CMSPage =>
      state.kind == 'news overview' || state.kind == 'person overview' || state.kind == 'search'
        ? fieldUpdater<CMSPage & { kind: 'news overview' | 'person overview' | 'standpoints overview' | 'ourview overview' | 'search' }>()('teasers')(updater)(state)
        : state
)
